import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box
} from '@mui/material';
import SortIndicator from './sortable-column-head';

/**
 * @typedef {Object} AgentSummaryRow
 * @property {string} userId - The unique identifier for the agent
 * @property {number} allocated - Number of allocated calls
 * @property {number} productive - Number of productive calls
 * @property {number} unProductive - Number of unproductive calls
 * @property {number} attempted - Number of attempted calls
 * @property {number} pending - Number of pending calls
 * @property {number} childAbove2Years - Number of calls for children above 2 years
 * @property {number} declined - Number of declined calls
 * @property {number} notInThePortal - Number of calls not in the portal
 * @property {number} productivityP - Productivity percentage
 * @property {number} attemptedP - Attempted percentage
 * @property {string} telecaller - Name of the telecaller/agent
 */

/**
 * @typedef {Object} SortMap
 * @property {('asc'|'desc'|null)} Allocations - Sort direction for allocations
 * @property {('asc'|'desc'|null)} Productive - Sort direction for productive calls
 * @property {('asc'|'desc'|null)} UnProductive - Sort direction for unproductive calls
 * @property {('asc'|'desc'|null)} Attempted - Sort direction for attempted calls
 * @property {('asc'|'desc'|null)} Pending - Sort direction for pending calls
 * @property {('asc'|'desc'|null)} ChildAbove2Years - Sort direction for child above 2 years
 * @property {('asc'|'desc'|null)} Declined - Sort direction for declined calls
 * @property {('asc'|'desc'|null)} NotInThePortal - Sort direction for not in portal
 * @property {('asc'|'desc'|null)} 'Productivity %' - Sort direction for productivity percentage
 * @property {('asc'|'desc'|null)} 'Attempted %' - Sort direction for attempted percentage
 */

// Column definitions with display names and corresponding data keys
const columns = [
  { key: 'Allocations', dataKey: 'allocated' },
  { key: 'Productive', dataKey: 'productive' },
  { key: 'UnProductive', dataKey: 'unProductive' },
  { key: 'Attempted', dataKey: 'attempted' },
  { key: 'Pending', dataKey: 'pending' },
  { key: 'ChildAbove2Years', dataKey: 'childAbove2Years' },
  { key: 'Declined', dataKey: 'declined' },
  { key: 'NotInThePortal', dataKey: 'notInThePortal' },
  { key: 'Productivity %', dataKey: 'productivityP' },
  { key: 'Attempted %', dataKey: 'attemptedP' }
];

/**
 * AgentSummaryTable - A sortable table component for displaying agent performance summary
 * 
 * This component renders a Material-UI table with agent performance data. The first column
 * (Agent) is sticky and displays the telecaller name. All other columns are sortable.
 * 
  * @param {Object} props - Component props
 * @param {AgentSummaryRow[]} props.rows - Array of agent summary data objects
 * @param {string} [props.maxHeight='30vh'] - Maximum height of the table container
 * @returns {JSX.Element} The rendered table component
 */
const AgentSummaryTable = ({
  rows = [],
  maxHeight = '30vh'
}) => {
  // Internal sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  /**
   * Handles sorting when a column header is clicked
   * @param {string} columnKey - The key of the column to sort
   * @param {('asc'|'desc'|null)} direction - The sort direction
   */
  const handleSort = (columnKey, direction) => {
    setSortConfig({
      key: columnKey,
      direction: direction
    });
  };

  /**
   * Creates a sort map for the SortIndicator components
   * @returns {SortMap} Object with sort directions for each column
   */
  const sortMap = useMemo(() => {
    const map = {
      'Allocations': null,
      'Productive': null,
      'UnProductive': null,
      'Attempted': null,
      'Pending': null,
      'ChildAbove2Years': null,
      'Declined': null,
      'NotInThePortal': null,
      'Productivity %': null,
      'Attempted %': null
    };

    if (sortConfig.key) {
      map[sortConfig.key] = sortConfig.direction;
    }

    return map;
  }, [sortConfig]);

  /**
   * Sorts the rows based on current sort configuration
   * @returns {AgentSummaryRow[]} Sorted array of rows
   */
  const sortedRows = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return rows;
    }

    const columnConfig = columns.find(col => col.key === sortConfig.key);
    if (!columnConfig) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[columnConfig.dataKey] || 0;
      const bValue = b[columnConfig.dataKey] || 0;

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting (for telecaller names if needed)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [rows, sortConfig]);


  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: maxHeight,
        overflowX: 'auto',
        overflowY: 'auto'
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {/* Sticky Agent Column */}
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 1001,
                backgroundColor: 'white',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                minWidth: '10rem',
                maxWidth: '10rem'
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#F28627',
                  fontSize: '1em'
                }}
              >
                Agent
              </Typography>
            </TableCell>

            {/* Sortable Data Columns */}
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align="center"
                sx={{ minWidth: '8rem' }}
              >
                <SortIndicator
                  direction={sortMap[column.key]}
                  label={column.key}
                  onClick={handleSort}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedRows.map((row, index) => (
            <TableRow key={row.userId || index} hover>
              {/* Sticky Agent Name Cell */}
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  minWidth: '10rem',
                  maxWidth: '10rem'
                }}
              >
                <Typography
                  sx={{
                    color: 'rgba(0, 0, 0, 0.7)',
                    fontSize: '0.9em',
                    wordWrap: 'break-word'
                  }}
                >
                  {row.telecaller || 'Unknown Agent'}
                </Typography>
              </TableCell>

              {/* Data Cells */}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align="center"
                >
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: '0.9em'
                    }}
                  >
                    {row[column.dataKey] || '0'}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}

          {/* Empty state */}
          {sortedRows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{ py: 4 }}
              >
                <Typography
                  sx={{
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontSize: '1em'
                  }}
                >
                  No agent data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AgentSummaryTable;
